import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ArrowDown, ArrowUp, MoreVertical } from 'lucide-react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface SortableHeaderCellProps<Key extends string> {
    columnKey: Key
    label: string
    sortable?: boolean
    isSortActive?: boolean
    sortDirection?: 'asc' | 'desc'
    align?: 'left' | 'center' | 'right'
    draggable?: boolean
    collapsible?: boolean
    /** Draws the light border that separates this header from the next column's. */
    bordered?: boolean
    /** Narrower side padding, to line up with the cells of a compact table. */
    compact?: boolean
    /** Pins the header to the right edge of the scroll area, above the cells that scroll beneath it. */
    pinnedEnd?: boolean
    onSortClick?: () => void
    onResizeStart: (event: React.MouseEvent) => void
    isResizing: boolean
    collapsed: boolean
    onToggleCollapsed: () => void
    onHide?: () => void
    canHide: boolean
}

export const SortableHeaderCell = <Key extends string>({
    columnKey,
    label,
    sortable = false,
    isSortActive = false,
    sortDirection = 'asc',
    align = 'left',
    draggable = true,
    collapsible = true,
    bordered = false,
    compact = false,
    pinnedEnd = false,
    onSortClick,
    onResizeStart,
    isResizing,
    collapsed,
    onToggleCollapsed,
    onHide,
    canHide,
}: SortableHeaderCellProps<Key>) => {
    const sortableColumn = useSortable({ id: columnKey, disabled: !draggable })
    const [menuOpen, setMenuOpen] = useState(false)
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null)
    const menuButtonRef = useRef<HTMLButtonElement | null>(null)
    const menuRef = useRef<HTMLDivElement | null>(null)
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(sortableColumn.transform),
        transition: sortableColumn.transition ?? 'transform 180ms cubic-bezier(0.2, 0, 0, 1)',
    }

    useEffect(() => {
        if (!menuOpen) return
        const handleOutsideClick = (event: MouseEvent) => {
            if (!(event.target instanceof Node)) return
            if (!menuButtonRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) setMenuOpen(false)
        }
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setMenuOpen(false)
        }
        document.addEventListener('mousedown', handleOutsideClick)
        document.addEventListener('keydown', handleEscape)
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick)
            document.removeEventListener('keydown', handleEscape)
        }
    }, [menuOpen])

    if (collapsed && collapsible) {
        return (
            <th
                ref={sortableColumn.setNodeRef}
                style={style}
                {...sortableColumn.attributes}
                {...sortableColumn.listeners}
                className={`relative h-9 select-none border-x border-border bg-surface p-0 ${draggable ? 'cursor-grab' : ''} ${
                    sortableColumn.isDragging ? 'cursor-grabbing opacity-50' : ''
                }`}
            >
                <button
                    type="button"
                    onClick={(event) => {
                        event.stopPropagation()
                        onToggleCollapsed()
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                    onPointerDown={(event) => event.stopPropagation()}
                    title={`Expand "${label}"`}
                    className="absolute inset-0 flex items-center justify-center text-foreground-muted transition-colors duration-150 hover:text-foreground"
                >
                    <MoreVertical className="size-3.5" strokeWidth={2} />
                </button>
            </th>
        )
    }

    const hasMenu = collapsible || (canHide && onHide)
    const alignmentClass = align === 'right' ? 'justify-end text-right' : align === 'center' ? 'justify-center text-center' : 'text-left'
    const openMenu = () => {
        const rect = menuButtonRef.current?.getBoundingClientRect()
        if (!rect) return
        const menuWidth = 180
        setMenuPosition({ top: rect.bottom + 4, left: Math.max(8, Math.min(window.innerWidth - menuWidth - 8, rect.right - menuWidth)) })
        setMenuOpen(true)
    }

    return (
        <th
            ref={sortableColumn.setNodeRef}
            style={style}
            className={`group ${
                pinnedEnd
                    ? 'sticky right-0 z-[1] before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:w-px before:bg-border'
                    : 'relative'
            } select-none whitespace-nowrap bg-surface ${compact ? 'px-3' : 'px-4'} py-2 text-left text-[13px] font-medium leading-[1.35] text-foreground-muted ${
                bordered ? 'border-r border-border last:border-r-0' : ''
            } ${sortableColumn.isDragging ? 'opacity-50' : ''}`}
        >
            <div
                {...sortableColumn.attributes}
                {...sortableColumn.listeners}
                onClick={() => sortable && onSortClick?.()}
                className={`flex touch-none items-center gap-1 truncate ${alignmentClass} ${hasMenu ? 'pr-6' : ''} ${
                    draggable ? (sortableColumn.isDragging ? 'cursor-grabbing' : 'cursor-grab') : ''
                } ${sortable ? 'transition-colors duration-150 hover:text-foreground' : ''}`}
                title={sortable ? 'Click to sort · drag to reorder' : draggable ? 'Drag to reorder' : label}
            >
                <span className="truncate">{label}</span>
                {sortable && isSortActive && (
                    <span className="text-accent">
                        {sortDirection === 'asc' ? <ArrowUp className="size-3.5" strokeWidth={2} /> : <ArrowDown className="size-3.5" strokeWidth={2} />}
                    </span>
                )}
            </div>
            {hasMenu && (
                <button
                    ref={menuButtonRef}
                    type="button"
                    aria-label={`${label} column options`}
                    onClick={(event) => {
                        event.stopPropagation()
                        if (menuOpen) setMenuOpen(false)
                        else openMenu()
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                    onPointerDown={(event) => event.stopPropagation()}
                    className="absolute right-2 top-1/2 z-20 inline-flex size-5 -translate-y-1/2 items-center justify-center rounded-md text-foreground-muted opacity-0 transition-opacity hover:bg-default hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
                >
                    <MoreVertical className="size-3.5" strokeWidth={2} />
                </button>
            )}
            {menuOpen && menuPosition
                ? createPortal(
                      <div
                          ref={menuRef}
                          style={{ position: 'fixed', top: menuPosition.top, left: menuPosition.left, width: 180 }}
                          className="z-[60] rounded-xl bg-surface p-1 shadow-overlay"
                      >
                          {collapsible && (
                              <button
                                  type="button"
                                  onClick={() => {
                                      setMenuOpen(false)
                                      onToggleCollapsed()
                                  }}
                                  className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-sm text-foreground hover:bg-default"
                              >
                                  Collapse column
                              </button>
                          )}
                          {canHide && onHide && (
                              <button
                                  type="button"
                                  onClick={() => {
                                      setMenuOpen(false)
                                      onHide()
                                  }}
                                  className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-sm text-foreground hover:bg-default"
                              >
                                  Hide column
                              </button>
                          )}
                      </div>,
                      document.body
                  )
                : null}
            <span
                role="separator"
                aria-orientation="vertical"
                onMouseDown={onResizeStart}
                onClick={(event) => event.stopPropagation()}
                onPointerDown={(event) => event.stopPropagation()}
                className={`absolute right-0 top-0 z-10 h-full w-1.5 cursor-col-resize select-none ${isResizing ? 'bg-accent/60' : 'hover:bg-accent/40'}`}
            >
                {/* The short divider that shows where a column can be resized, as in the Ongoing Loads header; a bordered table already draws a full-height one. */}
                {!bordered && <span aria-hidden className="absolute right-0 top-1/2 h-4 w-px -translate-y-1/2 bg-border" />}
            </span>
        </th>
    )
}
